// Sección "Voz y guion" del modo "Video con influencer" (V2). Se inyecta en
// el `ConfigurePanel` cuando `showGancho` (igual que `GanchoSection`), en
// desktop como bloque propio y en mobile debajo del Gancho.
//
// Controles (ADR-525/528/530/544/545):
//   • Guion — 3 modos: Automático (default, el backend lo genera a partir del
//     gancho), Escribir (textarea) o Grabar (DialogueAudioInput con uploader
//     propio al bucket `voces`, ADR-526 — este modo no tiene proyecto).
//   • Voz — fila que abre el `VoicePickerV2` (Atlas + Mis voces), DEBAJO del
//     textarea/grabador y SOLO en Escribir/Grabar (en Automático el backend
//     elige también la voz, ADR-545). En esos modos es OBLIGATORIA: sin voz
//     el Generar se deshabilita; default = última voz usada (localStorage).
//   • Forma de hablar — textarea con la dirección de actuación para Gemini
//     TTS (tono/ritmo/acento, default UGC rioplatense), SOLO con guion
//     "Escribir". Reemplaza al slider de velocidad (ADR-558).
//   • Duración — barra rellena debajo del Guion, SOLO visible con guion
//     Automático (con guion escrito/grabado la duración la marca el guion).
//     Reemplaza al botón "Configurar duración" + VideoConfigSheet en este
//     modo (ADR-528).
//
// El estado vive arriba (InterfazV2) agrupado en un solo `VoiceGuionValue`
// para no engordar la cadena de props (ADR-527). La duración usa el
// `videoDuration` existente de InterfazV2 (props aparte).

import React, { useState } from 'react';
import { ChevronRight, Mic, Pencil, Sparkles } from 'lucide-react';
import DialogueAudioInput from '../../../components/DialogueAudioInput';
import { uploadGuionAudio } from '../../../lib/guionAudioUpload';
import { VoicePickerV2 } from './VoicePickerV2';
import { FillBarSliderV2 } from './FillBarSliderV2';
import {
  VIDEO_INFLUENCER_DURATION_MIN,
  VIDEO_INFLUENCER_DURATION_MAX,
} from './VideoConfigSheet';
import { CaptionStylePickerV2 } from './CaptionStylePickerV2';
import {
  CAPTION_STYLES,
  DEFAULT_CAPTION_ID,
} from '../../../constants/captionStyles';

export type GuionMode = 'auto' | 'texto' | 'audio';

export interface VoiceGuionValue {
  /** Voz elegida: NOMBRE de la voz Gemini (`voces_google.nombre`, ADR-550).
   *  null = todavía sin elegir — la voz es OBLIGATORIA para generar
   *  (ADR-544); el default sale de la última voz usada (localStorage). */
  vozId: string | null;
  /** Nombre para mostrar en la fila "Voz". */
  vozNombre: string | null;
  /** Forma de hablar: dirección de actuación en lenguaje natural para el
   *  TTS de Gemini (tono/ritmo/acento). Se manda como `estilo_voz` con guion
   *  escrito. Reemplaza al slider de velocidad (ADR-558). */
  formaDeHablar: string;
  /** Modo del guion. Se manda como `dialogo_input_mode`. */
  guionMode: GuionMode;
  /** Guion escrito (modo 'texto'). Se manda como `dialogo`. */
  guionTexto: string;
  /** Audio grabado/subido (modo 'audio'). `url` firmada → `audio_url`. */
  guionAudio: { url: string; storagePath: string; durationSec: number } | null;
  /** Subtítulos incrustados en el video (réplica del flujo V1, ADR-537).
   *  Si true, viaja `caption_id` en el payload. */
  subtitulos: boolean;
  /** Estilo de subtítulos elegido (`constants/captionStyles`). */
  captionId: string;
}

// Sanea la dirección de actuación antes de mandarla como `estilo_voz`: en
// n8n el valor se interpola A MANO dentro de un string de otro JSON (el
// prompt del TTS), así que saltos de línea, comillas (rectas y tipográficas),
// backticks o barras romperían ese template. Se colapsa whitespace y se
// eliminan esos caracteres (ADR-559).
export function sanitizeEstiloVoz(s: string): string {
  return s
    .replace(/[\r\n]+/g, ' ')
    .replace(/["'“”‘’`´\\]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Dirección de actuación por defecto — matchea el estilo de los previews
// (ADR-557) y el target del modo: videos UGC en español rioplatense.
export const FORMA_DE_HABLAR_DEFAULT =
  'Tono cálido y cercano, ritmo natural de conversación, como si fuera una creadora UGC en un video, acento español argentino rioplatense';

export const VOICE_GUION_DEFAULT: VoiceGuionValue = {
  vozId: null,
  vozNombre: null,
  formaDeHablar: FORMA_DE_HABLAR_DEFAULT,
  guionMode: 'auto',
  guionTexto: '',
  guionAudio: null,
  // ON por default (pedido del usuario — V1 arrancaba apagado). ADR-538.
  subtitulos: true,
  captionId: DEFAULT_CAPTION_ID,
};

/** true si falta algo para poder generar: guion Escribir/Grabar sin
 *  completar, o voz sin elegir en esos modos (obligatoria ahí, ADR-544/545;
 *  en Automático el backend elige también la voz). El Generar se deshabilita
 *  para no mandar una intención ambigua al backend. */
export function isVoiceGuionIncomplete(v: VoiceGuionValue): boolean {
  return (
    (v.guionMode !== 'auto' && v.vozId === null) ||
    (v.guionMode === 'texto' && v.guionTexto.trim() === '') ||
    (v.guionMode === 'audio' && v.guionAudio === null)
  );
}

// Última voz usada en una generación exitosa del modo — se persiste en
// localStorage (la escribe InterfazV2 junto a `atlas-v2-last-products`) y se
// preselecciona al abrir el modo (ADR-544). Key con sufijo `-google`: las
// voces guardadas de la era ElevenLabs (`atlas-v2-last-voz`) son ids que el
// nuevo proveedor no entiende — no deben preseleccionarse (ADR-550).
const LAST_VOICE_KEY = 'atlas-v2-last-voz-google';

export function saveLastUsedVoice(id: string, nombre: string | null): void {
  try {
    localStorage.setItem(LAST_VOICE_KEY, JSON.stringify({ id, nombre }));
  } catch {
    /* noop */
  }
}

/** Default del estado con la última voz usada preseleccionada (si hay). */
export function getVoiceGuionDefault(): VoiceGuionValue {
  try {
    const raw = localStorage.getItem(LAST_VOICE_KEY);
    if (raw) {
      const v = JSON.parse(raw);
      if (v && typeof v.id === 'string' && v.id) {
        return {
          ...VOICE_GUION_DEFAULT,
          vozId: v.id,
          vozNombre: typeof v.nombre === 'string' ? v.nombre : null,
        };
      }
    }
  } catch {
    /* noop */
  }
  return VOICE_GUION_DEFAULT;
}

// Tope del guion grabado/subido en este modo (el editor usa 60s): el video
// dura como mucho 18s, un audio más largo que ~14s no entra (ADR-532).
export const GUION_AUDIO_MAX_SEC = 14;

// Costo base (créditos) de una generación con guion escrito/grabado; el
// resto es 1 crédito por segundo de duración efectiva (ADR-547).
export const INFLUENCER_GUION_BASE_COST = 4;

// Ritmo de lectura del TTS en español, en caracteres por segundo a velocidad
// 1.0. El editor usa 15 (para dimensionar placeholders visuales), pero
// calibrado contra una generación real quedó corto: un guion de ~181 chars a
// velocidad 1.1 estimaba ~11s y el audio real duró ~16s (⇒ ~11 chars/s).
// Como esta estimación define COSTO y `duracion`, preferimos quedar
// apenas por arriba (dispara el warning y capea en 14s) antes que cobrar
// de menos con un audio que desborda el video (ADR-549).
const GUION_CHARS_PER_SEC = 11;

// Estimación de duración hablada del guion escrito — misma forma que
// `estimateSpeechDuration` del Director de Estudio (+0.5s de cola para
// pausas/respiración) con el ritmo recalibrado de arriba. Sin factor de
// velocidad: el slider se eliminó (el ritmo ahora se pide en lenguaje
// natural vía "Forma de hablar", ADR-558) y estimamos a ritmo nominal.
export function estimateGuionSpeechSeconds(texto: string): number {
  const t = (texto || '').trim();
  if (!t) return 0;
  return t.length / GUION_CHARS_PER_SEC + 0.5;
}

/** Duración EFECTIVA del guion (segundos enteros, 1..14): duración del audio
 *  grabado, o estimación de lectura del texto. Viaja como `duracion` en el
 *  payload y define el costo (base 4 + duración, ADR-547). Devuelve null en
 *  guion Automático (ahí manda la barra de Duración) o si el input del modo
 *  todavía no está (texto vacío / sin audio — el Generar está bloqueado). */
export function getGuionDurationSec(v: VoiceGuionValue): number | null {
  if (v.guionMode === 'audio') {
    if (!v.guionAudio) return null;
    return Math.max(
      1,
      Math.min(GUION_AUDIO_MAX_SEC, Math.round(v.guionAudio.durationSec)),
    );
  }
  if (v.guionMode === 'texto') {
    const est = estimateGuionSpeechSeconds(v.guionTexto);
    if (est <= 0) return null;
    return Math.max(1, Math.min(GUION_AUDIO_MAX_SEC, Math.round(est)));
  }
  return null;
}

// "Grabar" OCULTO desde ADR-551: Gemini TTS no soporta speech-to-speech (no
// puede re-vocalizar un audio grabado). El flujo completo (DialogueAudioInput,
// upload al bucket, payload audio_url) queda en el código por si el proveedor
// lo permite a futuro — para reactivarlo, poner true.
const GRABAR_ENABLED = false;

const GUION_MODES: { key: GuionMode; label: string; Icon: typeof Sparkles }[] = [
  { key: 'auto', label: 'Automático', Icon: Sparkles },
  { key: 'texto', label: 'Escribir', Icon: Pencil },
  { key: 'audio', label: 'Grabar', Icon: Mic },
];

const VISIBLE_GUION_MODES = GUION_MODES.filter(
  (m) => GRABAR_ENABLED || m.key !== 'audio',
);

export interface VoiceGuionSectionProps {
  /** Clase del wrapper. Default `mt-5` (uso suelto, ej. mobile); pasá `''`
   *  cuando va dentro de un contenedor con `gap` propio (desktop). */
  className?: string;
  /** Empresa activa — destino del audio grabado (bucket `voces`). */
  companyId: string | null;
  value: VoiceGuionValue;
  onChange: (next: VoiceGuionValue) => void;
  /** Duración del video (segundos) — el `videoDuration` de InterfazV2. La
   *  barra solo se muestra con guion Automático (ADR-528). */
  videoDuration?: number;
  onVideoDurationChange?: (v: number) => void;
  /** Prefijo de ids DOM para la fila Voz (`<prefijo>-voz-row`) y el bloque
   *  Guion (`<prefijo>-guion-block`) — targets del scroll+shake de los
   *  botones Generar bloqueados (ADR-552/553). DEBE ser distinto por
   *  variante (ej. `influencer-desktop` / `influencer-mobile`): el sheet
   *  monta ambos paneles a la vez (ocultos por CSS) y un id duplicado haría
   *  que getElementById devuelva el nodo oculto. */
  domIdPrefix?: string;
}

export const VoiceGuionSection: React.FC<VoiceGuionSectionProps> = ({
  className = 'mt-5',
  companyId,
  value,
  onChange,
  videoDuration,
  onVideoDurationChange,
  domIdPrefix,
}) => {
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [captionPickerOpen, setCaptionPickerOpen] = useState(false);
  const selectedCaptionCategory =
    CAPTION_STYLES.find((c) => c.id === value.captionId)?.category ?? 'Simple';

  return (
    <div className={`${className} space-y-2.5`.trim()}>
      {/* Guion — segmented control de 3 modos. Sin card con borde (ADR-531):
          label afuera + controles sueltos, como las barras. */}
      <div id={domIdPrefix ? `${domIdPrefix}-guion-block` : undefined}>
        <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
          Guion
        </p>
        <div
          className={`grid gap-1 p-1 rounded-xl bg-[#F7F7F9] ${
            VISIBLE_GUION_MODES.length === 3 ? 'grid-cols-3' : 'grid-cols-2'
          }`}
        >
          {VISIBLE_GUION_MODES.map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => onChange({ ...value, guionMode: key })}
              className={`inline-flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-[12.5px] font-medium transition-colors ${
                value.guionMode === key
                  ? 'bg-white text-brand-black/90 shadow-sm'
                  : 'text-brand-black/55 hover:text-brand-black/75'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {value.guionMode === 'auto' && (
          <p className="mt-2.5 text-[12.5px] text-[#3C3C43]/45">
            Escribimos el guion por vos a partir del gancho y tu producto.
          </p>
        )}

        {value.guionMode === 'texto' && (
          <>
            {/* Subtítulo propio del textarea del guion — simetría con
                "Tono de voz" de abajo. */}
            <p className="mt-3 mb-1.5 text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              Guion
            </p>
            <textarea
              value={value.guionTexto}
              onChange={(e) =>
                onChange({ ...value, guionTexto: e.target.value })
              }
              placeholder="Escribí lo que va a decir el influencer…"
              rows={4}
              className="w-full px-3.5 py-3 rounded-xl bg-white border border-neutral-200 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors resize-none"
            />
            {/* Warning chiquito cuando el guion excede lo que entra en el
                video (~14s leídos, ADR-547). No bloquea: la duración/costo
                se capean en 14s igual. */}
            {estimateGuionSpeechSeconds(value.guionTexto) >
              GUION_AUDIO_MAX_SEC && (
              <p className="mt-1.5 text-[12px] text-[#B45309]">
                El guion puede ser demasiado largo para la duración máxima del
                video — acortalo para que entre completo.
              </p>
            )}
          </>
        )}

        {value.guionMode === 'audio' && (
          <div className="mt-2.5">
            <DialogueAudioInput
              value={
                value.guionAudio
                  ? { ...value.guionAudio, assetId: '' }
                  : null
              }
              onChange={(audio) =>
                onChange({
                  ...value,
                  guionAudio: audio
                    ? {
                        url: audio.url,
                        storagePath: audio.storagePath,
                        durationSec: audio.durationSec,
                      }
                    : null,
                })
              }
              projectId={null}
              companyId={companyId}
              uploader={
                companyId
                  ? (file) => uploadGuionAudio(file, companyId)
                  : undefined
              }
              maxDurationSec={GUION_AUDIO_MAX_SEC}
              hideFormatsHint
            />
          </div>
        )}
      </div>

      {/* Voz — debajo del textarea/grabador, SOLO en Escribir/Grabar: es la
          voz que va a leer el guion escrito o re-vocalizar el grabado. En
          Automático el backend elige también la voz (ADR-545). */}
      {value.guionMode !== 'auto' && (
        <button
          type="button"
          id={domIdPrefix ? `${domIdPrefix}-voz-row` : undefined}
          onClick={() => setVoicePickerOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-[#1644EB]/40 active:bg-neutral-50 transition-colors"
        >
          <span className="text-[13.5px] text-[#3C3C43]/70">Voz</span>
          <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-black/85">
            <span className="truncate max-w-[160px]">
              {value.vozNombre ?? 'Elegir'}
            </span>
            <ChevronRight className="w-3.5 h-3.5 text-[#3C3C43]/40 shrink-0" />
          </span>
        </button>
      )}

      {/* Forma de hablar — debajo de la Voz, SOLO con guion "Escribir": la
          dirección de actuación en lenguaje natural que Gemini TTS aplica al
          leer el guion (tono/ritmo/acento). Reemplaza al slider de velocidad
          (ADR-558). Viene con un default UGC rioplatense, editable. */}
      {value.guionMode === 'texto' && (
        <div>
          <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
            Tono de voz
          </p>
          {/* En mobile el container es más alto (el default rioplatense ocupa
              ~3 líneas en pantallas angostas); en md+ vuelve al compacto. */}
          <textarea
            value={value.formaDeHablar}
            onChange={(e) =>
              onChange({ ...value, formaDeHablar: e.target.value })
            }
            placeholder="Ej: tono enérgico, ritmo pausado, acento neutro…"
            rows={2}
            className="w-full min-h-[110px] md:min-h-0 px-3.5 py-3 rounded-xl bg-white border border-neutral-200 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors resize-none"
          />
        </div>
      )}

      {/* Duración — debajo del Guion, SOLO con guion Automático: con guion
          escrito/grabado la duración la marca el propio guion (ADR-528). */}
      {value.guionMode === 'auto' &&
        videoDuration !== undefined &&
        onVideoDurationChange && (
          <FillBarSliderV2
            label="Duración"
            min={VIDEO_INFLUENCER_DURATION_MIN}
            max={VIDEO_INFLUENCER_DURATION_MAX}
            step={1}
            value={videoDuration}
            onChange={onVideoDurationChange}
            formatValue={(v) => `${v}s`}
          />
        )}

      {/* Subtítulos — toggle + estilo (réplica del flujo V1, ADR-537). El
          estilo solo se muestra con el toggle activo; `caption_id` viaja en
          el payload únicamente en ese caso. */}
      <div className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200">
        <span className="text-[13.5px] text-[#3C3C43]/70">Subtítulos</span>
        <button
          type="button"
          role="switch"
          aria-checked={value.subtitulos}
          onClick={() => onChange({ ...value, subtitulos: !value.subtitulos })}
          className={`relative w-[51px] h-[31px] rounded-full transition-colors duration-200 shrink-0 ${
            value.subtitulos ? 'bg-brand-primary' : 'bg-black/[0.09]'
          }`}
        >
          <div
            className={`absolute top-[2px] w-[27px] h-[27px] bg-white rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.15)] transition-transform duration-200 ${
              value.subtitulos ? 'translate-x-[22px]' : 'translate-x-[2px]'
            }`}
          />
        </button>
      </div>
      {value.subtitulos && (
        <button
          type="button"
          onClick={() => setCaptionPickerOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-[#1644EB]/40 active:bg-neutral-50 transition-colors"
        >
          <span className="text-[13.5px] text-[#3C3C43]/70">
            Estilo de subtítulos
          </span>
          <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-black/85">
            {selectedCaptionCategory}
            <ChevronRight className="w-3.5 h-3.5 text-[#3C3C43]/40" />
          </span>
        </button>
      )}

      {/* Picker de estilo de subtítulos */}
      <CaptionStylePickerV2
        open={captionPickerOpen}
        onClose={() => setCaptionPickerOpen(false)}
        selectedCaptionId={value.captionId}
        onSelect={(id) => onChange({ ...value, captionId: id })}
      />

      {/* Picker de voz — bottom sheet mobile / panel lateral desktop */}
      <VoicePickerV2
        open={voicePickerOpen}
        onClose={() => setVoicePickerOpen(false)}
        selectedVozId={value.vozId}
        onSelect={(vozId, nombre) =>
          onChange({ ...value, vozId, vozNombre: nombre })
        }
      />
    </div>
  );
};
